'use client';

import { useTimeout } from '@intigral/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

interface AnchoredPopupOptions {
    enabled?: boolean;
    showDelay?: number;
    hideDelay?: number;
    onHoverChange?: (hovered: boolean) => void;
}

export const useAnchoredPopup = ({
    enabled = true,
    showDelay = 1250,
    hideDelay = 300,
    onHoverChange,
}: AnchoredPopupOptions = {}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const [showPopup, setShowPopup] = useState(false);
    const [hovered, setHovered] = useState(false);

    const { start: startShowTimeout, clear: clearShowTimeout } = useTimeout(() => setShowPopup(true), showDelay);
    const { start: startHideTimeout, clear: clearHideTimeout } = useTimeout(() => setShowPopup(false), hideDelay);

    const positionPopup = useCallback(() => {
        if (!cardRef.current || !popupRef.current) return;

        const cardRect = cardRef.current.getBoundingClientRect();
        const popupRect = popupRef.current.getBoundingClientRect();

        const popupInitialX = cardRect.left + cardRect.width / 2 - popupRect.width / 2;
        const popupInitialY = cardRect.top + cardRect.height / 2 - popupRect.height / 2;

        const adjustedLeft = Math.min(Math.max(popupInitialX, 0), window.innerWidth - popupRect.width);
        const adjustedTop = Math.min(Math.max(popupInitialY, 0), window.innerHeight - popupRect.height);

        popupRef.current.style.left = `${adjustedLeft}px`;
        popupRef.current.style.top = `${adjustedTop}px`;
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const handleScroll = () => setShowPopup(false);
        window.addEventListener('scroll', handleScroll);

        if (hovered) {
            clearHideTimeout();
            startShowTimeout();
        } else {
            clearShowTimeout();
            startHideTimeout();
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearShowTimeout();
            clearHideTimeout();
        };
    }, [enabled, hovered, startShowTimeout, startHideTimeout, clearShowTimeout, clearHideTimeout]);

    // Timer, not rAF: rAF may never fire in throttled tabs
    useEffect(() => {
        if (!enabled || !(showPopup || hovered)) return;

        const schedulePosition = (attempt = 0) => {
            const run = () => {
                if (cardRef.current && popupRef.current) {
                    positionPopup();
                    return;
                }
                if (attempt < 5) {
                    setTimeout(() => schedulePosition(attempt + 1), 16 * (attempt + 1));
                }
            };
            if (attempt === 0) {
                setTimeout(run, 0);
            } else {
                run();
            }
        };

        schedulePosition();
    }, [enabled, showPopup, hovered, positionPopup]);

    const handleMouseEnter = useCallback(() => {
        setHovered(true);
        onHoverChange?.(true);
    }, [onHoverChange]);

    const handleMouseLeave = useCallback(() => {
        setHovered(false);
        onHoverChange?.(false);
    }, [onHoverChange]);

    const closePopup = useCallback(() => {
        clearShowTimeout();
        clearHideTimeout();
        setHovered(false);
        setShowPopup(false);
        onHoverChange?.(false);
    }, [clearShowTimeout, clearHideTimeout, onHoverChange]);

    return {
        cardRef,
        popupRef,
        showPopup,
        hovered,
        isOpen: showPopup || hovered,
        setShowPopup,
        closePopup,
        handleMouseEnter,
        handleMouseLeave,
    };
};
